import { useEffect, useRef, useState } from 'react';
import {
  Box, Chip, CircularProgress, IconButton, InputAdornment, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import { Download, Launch, Search } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { fetchRecentReportArchive, downloadReportArtifact } from '../../services/reportService';
import { formatBytesShort, timeAgo } from './testsPageUtils';
import type { ReportArchiveEntry, ReportArtifactKind } from '../../types/reportArtifact';
import type { TestListingRow } from '../../services/testListingService';

interface ReportArchiveTabProps {
  /** Navigate to the test a report belongs to (same URL builder as the tests table). */
  onOpenTest: (row: Pick<TestListingRow, 'ProjectId' | 'WorkOrderKey' | 'WorkOrderDetailKey'>) => void;
}

const KIND_FILTERS: Array<{ label: string; value: ReportArtifactKind | undefined }> = [
  { label: 'All', value: undefined },
  { label: 'Generated', value: 'generated' },
  { label: 'Issued', value: 'issued' },
];

/** Lab-wide report archive browser: every archived render and issued copy
 *  across all tests, newest first, server-paginated with search. */
export const ReportArchiveTab = ({ onOpenTest }: ReportArchiveTabProps) => {
  const theme = useTheme();
  const [rows, setRows] = useState<ReportArchiveEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [kind, setKind] = useState<ReportArtifactKind | undefined>(undefined);
  const [search, setSearch] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchRecentReportArchive({ limit: rowsPerPage, offset: page * rowsPerPage, kind, search: search || undefined })
      .then((result) => {
        if (cancelled) return;
        setRows(result.rows);
        setTotal(result.total);
      })
      .catch(() => { /* surfaced by the global interceptor */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, rowsPerPage, kind, search]);

  const handleSearchChange = (value: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => { setSearch(value); setPage(0); }, 300);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Search file, test, project, WO, sample, user…"
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: 320, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.82rem' } }}
        />
        {KIND_FILTERS.map((f) => (
          <Chip
            key={f.label}
            size="small"
            clickable
            label={f.label}
            variant={kind === f.value ? 'filled' : 'outlined'}
            onClick={() => { setKind(f.value); setPage(0); }}
            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
          />
        ))}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {total} archived {total === 1 ? 'report' : 'reports'}
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '10px', overflow: 'hidden' }}>
        {/* Container-scroll (matches TestsTable) so stickyHeader anchors to the
            container, not the page — immune to AppShell's overflow ancestor. */}
        <TableContainer sx={{ maxHeight: 'calc(100vh - 230px)', minHeight: 300 }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              '& thead th': { py: 0.5, px: 1.25, zIndex: 2 },
              '& tbody td': { py: 0.3, px: 1.25 },
            }}
          >
            <TableHead>
              <TableRow>
                {['Kind', 'File', 'Test', 'Project', 'Work Order', 'Sample', 'By', 'When', 'Size', ''].map((h) => (
                  <TableCell
                    key={h || 'actions'}
                    sx={{ fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap', backgroundColor: theme.palette.background.paper }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={26} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      No archived reports yet — generating or issuing a report records it here.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.map((row) => {
                const issued = row.kind === 'issued';
                const accent = issued ? theme.customColors.maroon : theme.palette.primary.main;
                return (
                  <TableRow key={row.reportArtifactKey} hover>
                    <TableCell>
                      <Chip
                        size="small"
                        label={issued ? 'Issued' : 'Generated'}
                        sx={{
                          height: 20, fontSize: '0.68rem', fontWeight: 700,
                          color: issued ? theme.customColors.maroon : theme.palette.primary.dark,
                          backgroundColor: alpha(accent, 0.12),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Tooltip title={`${row.fileName}${row.note ? ` — ${row.note}` : ''}`}>
                        <Typography noWrap sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                          {row.fileName}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{row.testName ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      <Tooltip title={row.projectName ?? ''}>
                        <span>{row.projectId ?? '—'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{row.workOrderId ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{row.sampleReference ?? row.sampleId ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', maxWidth: 150 }}>
                      <Typography noWrap sx={{ fontSize: 'inherit', color: 'text.secondary' }}>
                        {row.createdBy}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                      <Tooltip title={new Date(row.createdDate).toLocaleString()}>
                        <span>{timeAgo(row.createdDate)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                      {formatBytesShort(row.byteSize)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Download this copy">
                        <IconButton
                          size="small"
                          aria-label="Download archived report"
                          onClick={() => {
                            downloadReportArtifact(row.workOrderDetailKey, row.reportArtifactKey)
                              .catch(() => { /* surfaced by global interceptor */ });
                          }}
                        >
                          <Download sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Open the test">
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Open test"
                            disabled={!row.projectId || !row.workOrderKey}
                            onClick={() => onOpenTest({
                              ProjectId: row.projectId ?? '',
                              WorkOrderKey: row.workOrderKey ?? '',
                              WorkOrderDetailKey: row.workOrderDetailKey,
                            })}
                          >
                            <Launch sx={{ fontSize: 17 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </Box>
      </Paper>
    </Box>
  );
};
